/**
 * "2 uur geleden", "Gisteren", "Maandag" — de tijdregel op een berichtkaart (replica-spec §9.1).
 *
 * Waarom niet gewoon een datum: een bericht van vanochtend als "06/10/2026" dwingt de lezer om te
 * rekenen. Op een werkvloer is de vraag zelden "wanneer precies" maar "is dit nieuw". Pas voorbij
 * een week wordt de datum zelf weer het nuttigste antwoord.
 *
 * Pure functie met `nu` als argument, zodat ze te testen is zonder de klok te verzetten — dezelfde
 * aanpak als `bulletinPeriodLine` en `plannedMomentFor` (ADR-0064).
 */

export interface RelativeTimeLabels {
  justNow: string;
  /**
   * Met `{count}` erin — enkele accolades, en dat is geen slordigheid.
   *
   * Dubbele accolades zijn de plaatshouder van i18next zelf: die zou de bibliotheek al proberen te
   * vervangen voordat deze functie de tekst ziet, met een waarschuwing in de console en een lege
   * plek op het scherm. Eén paar accolades laat ze staan.
   */
  minutesAgo: string;
  hoursAgo: string;
  yesterday: string;
}

/**
 * Hoe "vandaag" eruitziet.
 *
 * `relative` ("2 uur geleden") op een berichtkaart: daar is de vraag "is dit nieuw".
 * `clock` ("09:02") in de gesprekkenlijst (berichten-spec §3.4): daar staan tien rijen onder
 * elkaar en is de vraag "in welke volgorde is dit gezegd" — tien keer "x uur geleden" leest als
 * tien sommen.
 *
 * Eén functie met twee vormen, en geen tweede functie ernaast: de regels voor gisteren, deze week
 * en ouder zijn precies dezelfde, en die horen op één plaats te staan.
 */
export type TodayFormat = 'relative' | 'clock';

const WEEKDAGEN = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'];

/**
 * @param iso Het tijdstip van publicatie.
 * @param now Nu.
 * @param labels De vertaalde teksten; `{count}` wordt vervangen.
 * @param locale Voor de datum voorbij een week, en voor de klok van vandaag.
 * @param today Of vandaag als "2 uur geleden" of als "09:02" leest.
 */
export function relativeTime(
  iso: string,
  now: Date,
  labels: RelativeTimeLabels,
  locale = 'nl-BE',
  today: TodayFormat = 'relative',
): string {
  const then = new Date(iso);

  /*
    Eerst de kalenderdag, dan pas de klok.

    Dat is de omgekeerde volgorde van wat voor de hand ligt, en ze doet ertoe: iets van gisteravond
    23:00 is om 08:00 "gisteren" en niet "negen uur geleden", ook al klopt dat tweede rekenkundig.
    Een medewerker die begint, deelt zijn dag in dagen in — niet in uren sinds een tijdstip.
  */
  const dagVan = (date: Date) =>
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86_400_000;
  const dagen = dagVan(now) - dagVan(then);

  if (dagen === 1) return labels.yesterday;
  if (dagen > 1 && dagen < 7) {
    const dag = WEEKDAGEN[then.getDay()] as string;
    return dag.charAt(0).toUpperCase() + dag.slice(1);
  }

  if (dagen === 0) {
    if (today === 'clock') {
      return then.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    }

    const minuten = Math.floor((now.getTime() - then.getTime()) / 60000);

    // Een bericht uit de toekomst bestaat niet, maar een klok die een minuut voorloopt wel. Dan is
    // "nu" het eerlijkste antwoord — en niet "over 1 minuut".
    if (minuten < 1) return labels.justNow;
    if (minuten < 60) return labels.minutesAgo.replace('{count}', String(minuten));
    return labels.hoursAgo.replace('{count}', String(Math.floor(minuten / 60)));
  }

  return then.toLocaleDateString(locale);
}
